/**
 * Every person in the world, drawn as one instanced mesh per body part.
 *
 * The player and every villager share this: thirty people are eleven draw
 * calls, not three hundred. Each frame the crowd composes a short chain of
 * joint rotations per character on the CPU — a few dozen matrix multiplies —
 * and writes the results into the instance buffers. No skinning, no animation
 * mixer, no per-character material.
 *
 * Clothing is per-instance colour. A villager who wears a skirt writes a zero
 * matrix into the shorts mesh and a real one into the skirt mesh; the unused
 * instance costs a vertex shader invocation per vertex and nothing else.
 *
 * Everyone dresses for the weather (`setWeather`, `core/character/clothing.ts`):
 * sleeves, a jacket and its hem, a hat, gloves and tights are parts and
 * colours put on over their usual clothes, and taken off again as it warms.
 */

import type { InstancedMesh as InstancedMeshType } from 'three';
import {
  Color,
  Euler,
  Group,
  InstancedMesh,
  Matrix4,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
} from 'three';
import { BELLY, CHEST, HEAD_CENTRE, HIP_DROP, PALM, TOE } from '../../core/character/body.js';
import type { ClothingWeather, Dress } from '../../core/character/clothing.js';
import { dressFor } from '../../core/character/clothing.js';
import type { GaitPose } from '../../core/character/gait.js';
import { J, POINTS } from '../../core/character/ragdoll.js';
import type { PartGeometries } from './rig.js';
import { BODY, createPartGeometries } from './rig.js';
import { applyStreetLampLight } from '../terrainMaterial.js';

export type Outfit = 'shorts' | 'trousers' | 'skirt';

export interface CharacterLook {
  skin: number;
  hair: number;
  shirt: number;
  bottoms: number;
  shoes: number;
  hairStyle: 'short' | 'long';
  outfit: Outfit;
  /** A uniform's peaked cap, in this colour, over the hair. */
  cap?: number;
  /** Their jacket's colour, when the weather calls for one; else one from the wardrobe. */
  coat?: number;
}

/** What someone has on for the weather, over their usual clothes. */
interface Worn {
  /** What the legs show as: trousers put on for the cold, shorts on a hot day. */
  outfit: Outfit;
  sleeves: boolean;
  coat: boolean;
  beanie: boolean;
}

export interface CharacterPlacement {
  x: number;
  y: number;
  z: number;
  /** Facing, in the sense of `Object3D.rotation.y`: forward is (-sin, 0, -cos). */
  yaw: number;
  pose: GaitPose;
  /**
   * Whole-body pitch about the hips. Positive tips the head forward — a
   * skydiver lies at a right angle.
   */
  bodyPitch?: number;
  visible: boolean;
}

/**
 * A rider on a machine, posed in the machine's own frame (x right, y up, z
 * back, origin on the ground): where the hips sit and how the body leans,
 * and where the hands and feet must be. The arms and legs are solved to
 * reach them, so a hand stays on its grip however the bars turn.
 *
 * Angles: `pitch` positive tips forward (the head forward, or down),
 * `roll` positive leans right, `yaw` and `twist` positive turn right.
 */
export interface RiderPose {
  hips: { x: number; y: number; z: number; pitch: number; roll: number; yaw: number };
  /** Relative to the hips. */
  torso: { pitch: number; roll: number; twist: number };
  /** Relative to the torso. */
  head: { pitch: number; yaw: number };
  /** Left, then right: where each palm closes. */
  hands: readonly [Vector3, Vector3];
  /** Which way each elbow points. */
  elbows: readonly [Vector3, Vector3];
  /** Where each ankle goes. */
  feet: readonly [Vector3, Vector3];
  /** Which way each knee points. */
  knees: readonly [Vector3, Vector3];
  /** Each foot's toe-up pitch against the machine. */
  toes: [number, number];
  helmet: boolean;
}

type PartName = keyof PartGeometries;

/** Each side's joints, as a ragdoll numbers them. */
const LEFT = { shoulder: J.shoulderL, elbow: J.elbowL, wrist: J.wristL, hip: J.hipL, knee: J.kneeL, ankle: J.ankleL, toe: J.toeL };
const RIGHT = { shoulder: J.shoulderR, elbow: J.elbowR, wrist: J.wristR, hip: J.hipR, knee: J.kneeR, ankle: J.ankleR, toe: J.toeR };

/** Parts drawn twice per character, left then right. */
const PAIRED: ReadonlySet<PartName> = new Set(['upperArm', 'forearm', 'sleeve', 'thigh', 'cuff', 'shin', 'foot']);

const material = new MeshStandardMaterial({
  color: 0xffffff,
  vertexColors: true,
  flatShading: true,
  roughness: 0.78,
  metalness: 0,
});
applyStreetLampLight(material);

const ZERO = new Matrix4().makeScale(0, 0, 0);
const IDENTITY = new Matrix4();

// Scratch objects. The crowd poses dozens of characters a frame; allocating
// matrices per joint would be thousands of short-lived objects a second.
const root = new Matrix4();
const hips = new Matrix4();
const torso = new Matrix4();
const limb = new Matrix4();
const child = new Matrix4();
const grandchild = new Matrix4();
const local = new Matrix4();
const quat = new Quaternion();
const euler = new Euler();
const offset = new Vector3();
const unit = new Vector3(1, 1, 1);
const color = new Color();
// Scratch for the rider: the chain in the machine's frame, then placed in the world.
const frameLocal = { hips: new Matrix4(), torso: new Matrix4(), head: new Matrix4(), upper: new Matrix4(), lower: new Matrix4(), foot: new Matrix4() };
const world = new Matrix4();
const ikS = new Vector3(), ikT = new Vector3(), ikE = new Vector3(), ikU = new Vector3(), ikL = new Vector3(), ikP = new Vector3();
const bx = new Vector3(), by = new Vector3(), bz = new Vector3();
// Scratch for a ragdoll: points, and the directions between them.
const rp = Array.from({ length: 6 }, () => new Vector3());
const ru = new Vector3(), ra = new Vector3(), rb = new Vector3();
const partX = { torso: new Vector3(), upper: new Vector3(), thigh: new Vector3() };
const torsoBack = new Vector3();
const rag = { hips: new Matrix4(), torso: new Matrix4(), head: new Matrix4(), upper: new Matrix4(), lower: new Matrix4(), foot: new Matrix4() };

/**
 * Two-bone reach: the upper bone from a joint on `parent`, the lower from its
 * end, bent so the tip lands on `target` with the middle joint toward `pole`.
 * Limbs hang along -Y from their joints. An arm's forearm folds toward its
 * -Z (forward), a leg's shin toward +Z (back) — the same senses as the gait's
 * `elbow` and `knee` — so the joint's point faces the pole either way. Out of
 * reach, the limb stretches straight toward the target.
 */
function reach(upper: Matrix4, lower: Matrix4, parent: Matrix4, jx: number, jy: number, jz: number,
  target: Vector3, pole: Vector3, a: number, b: number, kind: 'arm' | 'leg'): void {
  ikS.set(jx, jy, jz).applyMatrix4(parent);
  ikU.copy(target).sub(ikS);
  const d = Math.min(Math.max(ikU.length(), Math.abs(a - b) + 1e-3), (a + b) * 0.998);
  ikU.normalize();
  ikP.copy(pole).addScaledVector(ikU, -pole.dot(ikU));
  if (ikP.lengthSq() < 1e-10) ikP.set(0, 0, kind === 'arm' ? 1 : -1);
  ikP.normalize();
  const cosA = Math.max(-1, Math.min(1, (a * a + d * d - b * b) / (2 * a * d)));
  const sinA = Math.sqrt(1 - cosA * cosA);
  // The middle joint, and the reached point.
  ikE.copy(ikS).addScaledVector(ikU, cosA * a).addScaledVector(ikP, sinA * a);
  ikT.copy(ikS).addScaledVector(ikU, d);
  // The upper bone's frame: +Y back up the bone, Z in the plane of the bend.
  by.copy(ikS).sub(ikE).normalize();
  ikL.copy(ikT).sub(ikE).normalize();
  // The lower bone's direction, less its part along the upper: where it bends to.
  bz.copy(ikL).addScaledVector(by, -ikL.dot(by));
  if (bz.lengthSq() < 1e-10) bz.copy(ikP).multiplyScalar(-1);
  bz.normalize();
  if (kind === 'arm') bz.negate();
  bx.crossVectors(by, bz);
  upper.makeBasis(bx, by, bz).setPosition(ikS);
  const bend = Math.acos(Math.max(-1, Math.min(1, -by.dot(ikL))));
  joint(lower, upper, 0, -a, 0, kind === 'arm' ? bend : -bend, 0, 0);
}

/** A joint: translate to the pivot, then rotate in the given Euler order. */
function joint(out: Matrix4, parent: Matrix4, x: number, y: number, z: number,
  rx: number, ry: number, rz: number, order: 'XYZ' | 'ZXY' | 'YXZ' = 'XYZ'): Matrix4 {
  euler.set(rx, ry, rz, order);
  quat.setFromEuler(euler);
  offset.set(x, y, z);
  local.compose(offset, quat, unit);
  return out.multiplyMatrices(parent, local);
}

/**
 * A part's frame at (o) from where its ends are: +Y along `up`, and +Z as
 * near `back` as square to that allows. +X is then Y × Z, as the rig's are.
 */
function frameYZ(out: Matrix4, o: Vector3, up: Vector3, back: Vector3): Matrix4 {
  by.copy(up).normalize();
  bz.copy(back).addScaledVector(by, -back.dot(by));
  if (bz.lengthSq() < 1e-10) bz.set(0, 0, 1).addScaledVector(by, -by.z);
  bz.normalize();
  bx.crossVectors(by, bz);
  return out.makeBasis(bx, by, bz).setPosition(o);
}

/** As `frameYZ`, with +X as near `across` as square to `up` allows. */
function frameYX(out: Matrix4, o: Vector3, up: Vector3, across: Vector3): Matrix4 {
  by.copy(up).normalize();
  bx.copy(across).addScaledVector(by, -across.dot(by));
  if (bx.lengthSq() < 1e-10) bx.set(1, 0, 0).addScaledVector(by, -by.x);
  bx.normalize();
  bz.crossVectors(bx, by);
  return out.makeBasis(bx, by, bz).setPosition(o);
}

const at = (p: ArrayLike<number>, i: number, out: Vector3) => out.set(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]);

const fa = new Vector3(), fb = new Vector3();
/** Which way the toe lies from the ankle in the foot's own frame, and the front of the pelvis from between the hip joints in the hips'. */
const TOE_WAY = [TOE[1], TOE[2]] as const;
const BELLY_WAY = [BELLY[1] + HIP_DROP, BELLY[2]] as const;

/**
 * A part's frame at `o` from a direction `v` that lies along (0, y, z) in
 * the part's own frame (`way`), with +X as near `across` as square to it
 * allows: Y and Z worked back from where `v` points.
 */
function frameAlong(out: Matrix4, o: Vector3, v: Vector3, across: Vector3, way: readonly [number, number]): Matrix4 {
  const n = Math.hypot(way[0], way[1]), ty = way[0] / n, tz = way[1] / n;
  fa.copy(v).normalize();
  bx.copy(across).addScaledVector(fa, -across.dot(fa));
  if (bx.lengthSq() < 1e-10) bx.set(1, 0, 0);
  bx.normalize();
  fb.crossVectors(bx, fa);
  by.copy(fa).multiplyScalar(ty).addScaledVector(fb, -tz);
  bz.copy(fa).multiplyScalar(tz).addScaledVector(fb, ty);
  return out.makeBasis(bx, by, bz).setPosition(o);
}

export class CharacterCrowd {
  readonly group = new Group();
  private readonly meshes = new Map<PartName, InstancedMeshType>();
  private readonly looks: Array<CharacterLook | undefined>;
  /** What each has on for the weather. */
  private readonly worn: Worn[];
  private weather: ClothingWeather | undefined;
  private readonly free: number[] = [];
  /** Where each person's joints were last drawn, as a ragdoll's points: see `jointsOf`. */
  private readonly joints: Float32Array;

  constructor(readonly capacity: number) {
    const geometries = createPartGeometries();
    for (const name of Object.keys(geometries) as PartName[]) {
      const count = capacity * (PAIRED.has(name) ? 2 : 1);
      const mesh = new InstancedMesh(geometries[name], material, count);
      mesh.name = `crowd:${name}`;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      // Instances move every frame, so a bounding sphere computed once is
      // wrong a second later. Characters are only ever near the camera anyway.
      mesh.frustumCulled = false;
      for (let i = 0; i < count; i++) {
        mesh.setMatrixAt(i, ZERO);
        mesh.setColorAt(i, color.set(0xffffff));
      }
      this.meshes.set(name, mesh);
      this.group.add(mesh);
    }
    this.looks = new Array(capacity).fill(undefined);
    this.worn = Array.from({ length: capacity }, () => ({ outfit: 'trousers' as Outfit, sleeves: false, coat: false, beanie: false }));
    this.joints = new Float32Array(capacity * POINTS * 3);
    for (let i = capacity - 1; i >= 0; i--) this.free.push(i);
  }

  get size(): number {
    return this.capacity - this.free.length;
  }

  /** Claim a slot and dress it. Returns undefined when the crowd is full. */
  add(look: CharacterLook): number | undefined {
    const id = this.free.pop();
    if (id === undefined) return undefined;
    this.looks[id] = look;
    this.paint(id, look);
    return id;
  }

  remove(id: number): void {
    if (this.looks[id] === undefined) return;
    this.looks[id] = undefined;
    this.hide(id);
    this.free.push(id);
  }

  /** Dress everyone for `w`: called as the weather changes; the clothes go on at the next pose. */
  setWeather(w: ClothingWeather): void {
    this.weather = w;
    this.looks.forEach((look, id) => { if (look !== undefined) this.paint(id, look); });
  }

  private paint(id: number, look: CharacterLook): void {
    const worn = this.worn[id];
    const dress: Dress | undefined = this.weather === undefined ? undefined
      : dressFor(this.weather, seeded(look, 1) * 2 - 1, look.outfit === 'skirt', look.outfit === 'shorts');
    worn.outfit = dress === undefined ? look.outfit
      : dress.legs === 'covered' ? 'trousers'
        : dress.legs === 'bare' && look.outfit === 'trousers' ? 'shorts' : look.outfit;
    worn.sleeves = dress?.sleeves === 'long';
    worn.coat = dress?.coat === true;
    // A uniform's cap stays on, hat or not.
    worn.beanie = dress?.beanie === true && look.cap === undefined;
    // Not everyone has a bright rain jacket.
    const jacket = dress?.raincoat === true && seeded(look, 2) < 0.6 ? RAINCOAT[Math.floor(seeded(look, 3) * RAINCOAT.length)]
      : look.coat ?? COAT[Math.floor(seeded(look, 4) * COAT.length)];
    const top = worn.coat ? jacket : look.shirt;
    const legs = worn.outfit === 'trousers' ? look.bottoms : dress?.legs === 'tights' ? TIGHTS[Math.floor(seeded(look, 5) * TIGHTS.length)] : look.skin;
    const set = (name: PartName, hex: number) => {
      const mesh = this.meshes.get(name)!;
      if (PAIRED.has(name)) {
        mesh.setColorAt(id * 2, color.set(hex));
        mesh.setColorAt(id * 2 + 1, color.set(hex));
      } else {
        mesh.setColorAt(id, color.set(hex));
      }
      mesh.instanceColor!.needsUpdate = true;
    };
    set('head', look.skin);
    set('hairShort', look.hair);
    set('hairLong', look.hair);
    set('torso', top);
    set('hipsShorts', look.bottoms);
    set('hipsSkirt', look.bottoms);
    set('cuff', look.bottoms);
    set('upperArm', top);
    set('sleeve', top);
    set('hem', jacket);
    set('beanie', KNIT[Math.floor(seeded(look, 6) * KNIT.length)]);
    set('forearm', dress?.gloves === true ? GLOVES[Math.floor(seeded(look, 7) * GLOVES.length)] : look.skin);
    set('thigh', legs);
    set('shin', legs);
    set('foot', look.shoes);
    set('helmet', 0xffffff);
    set('cap', look.cap ?? 0xffffff);
  }

  private hide(id: number): void {
    for (const [name, mesh] of this.meshes) {
      if (PAIRED.has(name)) {
        mesh.setMatrixAt(id * 2, ZERO);
        mesh.setMatrixAt(id * 2 + 1, ZERO);
      } else {
        mesh.setMatrixAt(id, ZERO);
      }
    }
  }

  private put(name: PartName, index: number, matrix: Matrix4): void {
    this.meshes.get(name)!.setMatrixAt(index, matrix);
  }

  /** Note where joint `k` of person `id` is: (x, y, z) in the frame of part matrix `m`. */
  private mark(id: number, k: number, m: Matrix4, x = 0, y = 0, z = 0): void {
    offset.set(x, y, z).applyMatrix4(m);
    const o = (id * POINTS + k) * 3;
    this.joints[o] = offset.x; this.joints[o + 1] = offset.y; this.joints[o + 2] = offset.z;
  }

  /**
   * Where person `id`'s joints were last drawn, in the world, as a ragdoll's
   * points (`core/character/ragdoll.ts`, `J`): what a ragdoll starts from.
   */
  jointsOf(id: number, out = new Float32Array(POINTS * 3)): Float32Array {
    out.set(this.joints.subarray(id * POINTS * 3, (id + 1) * POINTS * 3));
    return out;
  }

  /**
   * Pose one character.
   *
   * The chain, root to tip: feet on the ground at (x, y, z) facing `yaw` →
   * hips, lifted by the gait's bob and leant → torso, counter-twisted → head,
   * arms and legs. Every rotation is in the parent joint's frame, which is what
   * lets a runner's lean carry the arms and head with it for free.
   */
  place(id: number, p: CharacterPlacement): void {
    const look = this.looks[id];
    if (look === undefined) return;
    if (!p.visible) {
      this.hide(id);
      return;
    }
    const pose = p.pose;

    joint(root, IDENTITY, p.x, p.y, p.z, 0, p.yaw, 0, 'YXZ');
    // Leaning forward tips the head toward -Z, which is a negative rotation
    // about X — see the sign conventions in `gait.ts`.
    joint(hips, root, 0, BODY.hip + pose.bob, 0, -(pose.lean + (p.bodyPitch ?? 0)), 0, pose.sway, 'XYZ');
    this.mark(id, J.belly, hips, BELLY[0], BELLY[1], BELLY[2]);

    const worn = this.worn[id];
    const skirt = worn.outfit === 'skirt';
    this.put('hipsShorts', id, skirt ? ZERO : hips);
    this.put('hipsSkirt', id, skirt ? hips : ZERO);
    this.put('hem', id, worn.coat ? hips : ZERO);

    joint(torso, hips, 0, BODY.waist, 0, 0, pose.twist, 0);
    this.put('torso', id, torso);
    this.mark(id, J.waist, torso);
    this.mark(id, J.chest, torso, CHEST[0], CHEST[1], CHEST[2]);

    joint(child, torso, 0, BODY.torsoHeight, 0, pose.nod, -pose.twist * 0.6, 0, 'YXZ');
    this.put('head', id, child);
    this.mark(id, J.neck, child);
    this.mark(id, J.head, child, 0, HEAD_CENTRE, 0);
    const long = look.hairStyle === 'long';
    this.put('hairShort', id, long ? ZERO : child);
    this.put('hairLong', id, long ? child : ZERO);
    this.put('helmet', id, ZERO);
    this.put('cap', id, look.cap !== undefined ? child : ZERO);
    this.put('beanie', id, worn.beanie ? child : ZERO);

    // side: -1 is the character's left, which is -X when facing -Z.
    for (const side of [-1, 1] as const) {
      const index = id * 2 + (side < 0 ? 0 : 1);
      const shoulder = side < 0 ? pose.shoulder.left : pose.shoulder.right;
      const elbow = side < 0 ? pose.elbow.left : pose.elbow.right;
      const j = side < 0 ? LEFT : RIGHT;
      const out = pose.spread + (pose.armOut === undefined ? 0 : side < 0 ? pose.armOut.left : pose.armOut.right);
      joint(limb, torso, side * BODY.shoulderWidth, BODY.shoulderHeight, 0, shoulder, 0, side * out, 'ZXY');
      this.put('upperArm', index, limb);
      this.mark(id, j.shoulder, limb);
      joint(child, limb, 0, -BODY.upperArm, 0, elbow, 0, 0);
      this.put('forearm', index, child);
      this.put('sleeve', index, worn.sleeves ? child : ZERO);
      this.mark(id, j.elbow, child);
      this.mark(id, j.wrist, child, 0, -PALM, 0);

      const thigh = side < 0 ? pose.thigh.left : pose.thigh.right;
      const knee = side < 0 ? pose.knee.left : pose.knee.right;
      const ankle = side < 0 ? pose.ankle.left : pose.ankle.right;
      joint(limb, hips, side * BODY.hipWidth, -HIP_DROP, 0, thigh, 0, side * 0.025);
      this.put('thigh', index, limb);
      this.put('cuff', index, worn.outfit === 'shorts' ? limb : ZERO);
      this.mark(id, j.hip, limb);
      joint(child, limb, 0, -BODY.thigh, 0, -knee, 0, 0);
      this.put('shin', index, child);
      this.mark(id, j.knee, child);
      joint(grandchild, child, 0, -BODY.shin, 0, ankle, 0, 0);
      this.put('foot', index, grandchild);
      this.mark(id, j.ankle, grandchild);
      this.mark(id, j.toe, grandchild, TOE[0], TOE[1], TOE[2]);
    }
  }

  /**
   * Pose one character on a machine whose world matrix is `frame`: hips,
   * torso and head as the pose says, arms and legs solved to reach the grips
   * and the pegs (see `reach`), feet set flat against the machine.
   */
  placeRider(id: number, frame: Matrix4, p: RiderPose): void {
    const look = this.looks[id];
    if (look === undefined) return;
    const L = frameLocal;
    const put = (name: PartName, index: number, local: Matrix4) =>
      this.put(name, index, world.multiplyMatrices(frame, local));

    joint(L.hips, IDENTITY, p.hips.x, p.hips.y, p.hips.z, -p.hips.pitch, -p.hips.yaw, -p.hips.roll, 'YXZ');
    this.mark(id, J.belly, world.multiplyMatrices(frame, L.hips), BELLY[0], BELLY[1], BELLY[2]);
    const worn = this.worn[id];
    const skirt = worn.outfit === 'skirt';
    this.put('hipsShorts', id, skirt ? ZERO : world.multiplyMatrices(frame, L.hips));
    this.put('hipsSkirt', id, skirt ? world.multiplyMatrices(frame, L.hips) : ZERO);
    this.put('hem', id, worn.coat ? world.multiplyMatrices(frame, L.hips) : ZERO);

    joint(L.torso, L.hips, 0, BODY.waist, 0, -p.torso.pitch, -p.torso.twist, -p.torso.roll, 'YXZ');
    put('torso', id, L.torso);
    this.mark(id, J.waist, world);
    this.mark(id, J.chest, world, CHEST[0], CHEST[1], CHEST[2]);
    joint(L.head, L.torso, 0, BODY.torsoHeight, 0, -p.head.pitch, -p.head.yaw, 0, 'YXZ');
    put('head', id, L.head);
    this.mark(id, J.neck, world);
    this.mark(id, J.head, world, 0, HEAD_CENTRE, 0);
    const long = look.hairStyle === 'long';
    this.put('hairShort', id, p.helmet || long ? ZERO : world.multiplyMatrices(frame, L.head));
    this.put('hairLong', id, !p.helmet && long ? world.multiplyMatrices(frame, L.head) : ZERO);
    this.put('helmet', id, p.helmet ? world.multiplyMatrices(frame, L.head) : ZERO);
    this.put('cap', id, !p.helmet && look.cap !== undefined ? world.multiplyMatrices(frame, L.head) : ZERO);
    this.put('beanie', id, !p.helmet && worn.beanie ? world.multiplyMatrices(frame, L.head) : ZERO);

    for (const side of [-1, 1] as const) {
      const i = side < 0 ? 0 : 1;
      const index = id * 2 + i;
      const j = side < 0 ? LEFT : RIGHT;
      reach(L.upper, L.lower, L.torso, side * BODY.shoulderWidth, BODY.shoulderHeight, 0,
        p.hands[i], p.elbows[i], BODY.upperArm, PALM, 'arm');
      put('upperArm', index, L.upper);
      this.mark(id, j.shoulder, world);
      put('forearm', index, L.lower);
      this.put('sleeve', index, worn.sleeves ? world : ZERO);
      this.mark(id, j.elbow, world);
      this.mark(id, j.wrist, world, 0, -PALM, 0);

      reach(L.upper, L.lower, L.hips, side * BODY.hipWidth, -HIP_DROP, 0, p.feet[i], p.knees[i], BODY.thigh, BODY.shin, 'leg');
      put('thigh', index, L.upper);
      this.mark(id, j.hip, world);
      this.put('cuff', index, worn.outfit === 'shorts' ? world.multiplyMatrices(frame, L.upper) : ZERO);
      put('shin', index, L.lower);
      this.mark(id, j.knee, world);
      // The foot square to the machine, on the ankle wherever the shin put it.
      offset.set(0, -BODY.shin, 0).applyMatrix4(L.lower);
      euler.set(p.toes[i], 0, 0);
      L.foot.compose(offset, quat.setFromEuler(euler), unit);
      put('foot', index, L.foot);
      this.mark(id, j.ankle, world);
      this.mark(id, j.toe, world, TOE[0], TOE[1], TOE[2]);
    }
  }

  /**
   * Pose one person from a ragdoll's points (`core/character/ragdoll.ts`),
   * world positions in the order of `J`. Each part hangs from its joint
   * toward the next, turned about its own length as the points beyond it
   * say: a bent elbow or knee bends the way the rig's do, a straight one
   * keeps the way the hand or the foot faces.
   */
  placeRagdoll(id: number, p: ArrayLike<number>, helmet: boolean): void {
    const look = this.looks[id];
    if (look === undefined) return;
    const [a, b, c, d] = rp;
    // The hips: right from hip to hip, forward to the front of the pelvis, the pivot a little above the hip joints.
    at(p, J.hipR, a).sub(at(p, J.hipL, b));
    at(p, J.hipL, c).add(at(p, J.hipR, d)).multiplyScalar(0.5);
    at(p, J.belly, b).sub(c);
    frameAlong(rag.hips, c, b, a, BELLY_WAY);
    ru.setFromMatrixColumn(rag.hips, 1);
    rag.hips.setPosition(c.addScaledVector(ru, HIP_DROP));
    const worn = this.worn[id];
    const skirt = worn.outfit === 'skirt';
    this.put('hipsShorts', id, skirt ? ZERO : rag.hips);
    this.put('hipsSkirt', id, skirt ? rag.hips : ZERO);
    this.put('hem', id, worn.coat ? rag.hips : ZERO);
    // The torso from the waist up the spine, square across the shoulders.
    at(p, J.shoulderR, a).sub(at(p, J.shoulderL, b));
    at(p, J.neck, ru).sub(at(p, J.waist, c));
    frameYX(rag.torso, c, ru, a);
    partX.torso.setFromMatrixColumn(rag.torso, 0);
    this.put('torso', id, rag.torso);
    at(p, J.head, ru).sub(at(p, J.neck, c));
    frameYX(rag.head, c, ru, partX.torso);
    const long = look.hairStyle === 'long';
    this.put('head', id, rag.head);
    this.put('hairShort', id, helmet || long ? ZERO : rag.head);
    this.put('hairLong', id, !helmet && long ? rag.head : ZERO);
    this.put('helmet', id, helmet ? rag.head : ZERO);
    this.put('cap', id, !helmet && look.cap !== undefined ? rag.head : ZERO);
    this.put('beanie', id, !helmet && worn.beanie ? rag.head : ZERO);
    torsoBack.setFromMatrixColumn(rag.torso, 2);

    for (const side of [0, 1] as const) {
      const j = side === 0 ? LEFT : RIGHT;
      const index = id * 2 + side;
      // The upper arm: the forearm folds toward its front (-Z); straight, the elbow points back as the torso's does.
      at(p, j.shoulder, a); at(p, j.elbow, b); at(p, j.wrist, c);
      ru.copy(a).sub(b);
      ra.copy(c).sub(b).negate().addScaledVector(torsoBack, 0.02);
      frameYZ(rag.upper, a, ru, ra);
      partX.upper.setFromMatrixColumn(rag.upper, 0);
      this.put('upperArm', index, rag.upper);
      ru.copy(b).sub(c);
      frameYX(rag.lower, b, ru, partX.upper);
      this.put('forearm', index, rag.lower);
      this.put('sleeve', index, worn.sleeves ? rag.lower : ZERO);
      // The thigh: the shin folds toward its back (+Z); straight, the knee faces the way the toes do.
      at(p, j.hip, a); at(p, j.knee, b); at(p, j.ankle, c); at(p, j.toe, d);
      ru.copy(a).sub(b);
      ra.copy(c).sub(b).addScaledVector(rb.copy(c).sub(d), 0.15);
      frameYZ(rag.upper, a, ru, ra);
      partX.thigh.setFromMatrixColumn(rag.upper, 0);
      this.put('thigh', index, rag.upper);
      this.put('cuff', index, worn.outfit === 'shorts' ? rag.upper : ZERO);
      ru.copy(b).sub(c);
      frameYX(rag.lower, b, ru, partX.thigh);
      this.put('shin', index, rag.lower);
      // The foot from the ankle to the toe, square to the knee's hinge.
      frameAlong(rag.foot, c, ra.copy(d).sub(c), partX.thigh, TOE_WAY);
      this.put('foot', index, rag.foot);
    }
    for (let k = 0; k < POINTS * 3; k++) this.joints[id * POINTS * 3 + k] = p[k];
  }

  /** Flag every instance buffer for upload. Call once a frame, after placing. */
  commit(): void {
    for (const mesh of this.meshes.values()) mesh.instanceMatrix.needsUpdate = true;
  }
}

// --- wardrobe ---------------------------------------------------------------

const SKIN = [0xf1c7a5, 0xe0ac86, 0xc98e64, 0xa56b45, 0x7b4c30, 0xf6d5bd];
const HAIR = [0xe8c56a, 0x6b4428, 0x2b1d14, 0x9a4a24, 0x1a1a1a, 0xb8b0a4, 0xd9a441];
// Saturated, as in the reference: white, sky, sunflower, grass, tomato, navy.
const SHIRT = [0xf4f1ea, 0x5ea4d8, 0xf3c33c, 0x4f9d4a, 0xd9483b, 0x2d4a78, 0xe98fb0, 0x3fb5a3, 0xf08a3c];
const BOTTOMS = [0x3a5a8c, 0x2b3d5c, 0xb49a6c, 0x2a2a2a, 0x6d4a35, 0x7a2d3a, 0x4c6b3a];
const SHOES = [0x2a2522, 0x4a3426, 0xefefef, 0x1f2a3a];
// For the weather: jackets mostly dark and muted, rain jackets bright,
// knitted hats any colour, gloves and tights dark.
const COAT = [0x2b3346, 0x1e1f24, 0x56606b, 0x8a7356, 0x3d5a45, 0x7a2f35, 0x2f4f78, 0xb7a58a];
const RAINCOAT = [0xf2c230, 0xe2552f, 0x3f8fd2, 0x2f8f5a];
const KNIT = [0xc9413a, 0x2b3a5c, 0xe8e2d6, 0x3f6e4a, 0xe39b2d, 0x6a6a72, 0x8e4a8a];
const GLOVES = [0x1f1f22, 0x3a2a20, 0x2b3346];
const TIGHTS = [0x1e1e24, 0x3a3540, 0x5a4a44];

/**
 * A number in 0..1 drawn from someone's looks, the same each time, for the
 * `k`th choice of what they wear: how hardy they are, which jacket.
 */
function seeded(look: CharacterLook, k: number): number {
  let h = (look.skin * 31 ^ look.hair * 17 ^ look.shirt * 7 ^ look.bottoms * 3 ^ look.shoes ^ k * 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** A deterministic outfit from a seed, so a villager keeps their clothes. */
export function lookFromSeed(seed: number): CharacterLook {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  const next = (n: number) => {
    h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
    h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
    h = (h ^ (h >>> 15)) >>> 0;
    return h % n;
  };
  const outfitRoll = next(10);
  const outfit: Outfit = outfitRoll < 4 ? 'shorts' : outfitRoll < 8 ? 'trousers' : 'skirt';
  return {
    skin: SKIN[next(SKIN.length)],
    hair: HAIR[next(HAIR.length)],
    shirt: SHIRT[next(SHIRT.length)],
    bottoms: BOTTOMS[next(BOTTOMS.length)],
    shoes: SHOES[next(SHOES.length)],
    hairStyle: outfit === 'skirt' || next(3) === 0 ? 'long' : 'short',
    outfit,
  };
}

/** A police officer: someone of their own, from a seed, in the country's uniform and peaked cap. */
export function officerLook(seed: number, uniform: { jacket: number; trousers: number; cap: number }): CharacterLook {
  const own = lookFromSeed(seed);
  return { ...own, shirt: uniform.jacket, coat: uniform.jacket, bottoms: uniform.trousers, shoes: 0x1a1a1a, hairStyle: 'short', outfit: 'trousers', cap: uniform.cap };
}

/** The player: easy to pick out of a crowd from the chase camera. */
export const PLAYER_LOOK: CharacterLook = {
  skin: 0xe8b48f,
  hair: 0x5a3620,
  shirt: 0xf0772f,
  // A jacket the colour of the shirt, so the player is as easy to pick out in winter.
  coat: 0xe0682a,
  bottoms: 0x3b5680,
  shoes: 0xf2f2f2,
  hairStyle: 'short',
  outfit: 'trousers',
};
